import { useState } from 'react'
import { TopBar } from '../components/TopBar'
import { useApp } from '../store/AppContext'
import { localISODate } from '../lib/date'
import { awardFoodBites } from '../lib/youthRewards'
import {
  isRoutineDueOnDate,
  loadRoutines,
  saveRoutines,
  type CompletionKind,
  type Routine,
  type RoutineDay,
  type RoutineRepeat,
  type RoutineStyle,
} from '../lib/dailyLiving'

const completionLabels: Record<CompletionKind, string> = {
  done: '✅ Done',
  with_help: '🤝 Did it with help',
  mostly_myself: '🌱 Mostly did it myself',
}

const dayOptions: { key: RoutineDay; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
]

export function Routines() {
  const { currentUser, interfaceMode, accessProfile } = useApp()
  const isYouth = currentUser.role === 'participant' && accessProfile.accountType === 'youth'
  const canCreate = !isYouth && ['participant', 'rep', 'support_worker', 'allied_health'].includes(currentUser.role)
  const [routines, setRoutines] = useState<Routine[]>(loadRoutines)
  const [showCreate, setShowCreate] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const visibleRoutines = isYouth ? routines.filter((routine) => isRoutineDueOnDate(routine)) : routines

  const persist = (next: Routine[]) => { setRoutines(next); saveRoutines(next) }

  const complete = (routineId: string, stepId: string, completionKind: CompletionKind = 'done') => {
    const routine = routines.find((item) => item.id === routineId)
    const step = routine?.steps.find((item) => item.id === stepId)
    if (isYouth && routine && step && !step.done) {
      const occurrence = localISODate()
      awardFoodBites(currentUser.id, 'routine_step', `${step.id}:${occurrence}`, 1)
      const otherStepsDone = routine.steps.filter((item) => item.id !== stepId).every((item) => item.done)
      if (otherStepsDone) awardFoodBites(currentUser.id, 'routine_complete', `${routine.id}:${occurrence}`, 5)
    }
    persist(routines.map((item) => item.id !== routineId ? item : {
      ...item,
      steps: item.steps.map((routineStep) => routineStep.id !== stepId ? routineStep : {
        ...routineStep,
        done: !routineStep.done,
        completionKind: !routineStep.done ? completionKind : undefined,
        completedByUserId: !routineStep.done ? currentUser.id : undefined,
        completedAt: !routineStep.done ? new Date().toISOString() : undefined,
      }),
    }))
  }

  const resetRoutine = (routineId: string) => {
    persist(routines.map((routine) => routine.id === routineId ? { ...routine, steps: routine.steps.map((step) => ({ ...step, done: false, completionKind: undefined, completedAt: undefined, completedByUserId: undefined })) } : routine))
  }

  return (
    <div className={isYouth ? 'min-h-screen bg-orange-50 pb-24' : ''}>
      <TopBar title={isYouth ? 'My Routines 🔄' : 'Routines'} subtitle={isYouth ? 'One step at a time' : 'Step-by-step support for everyday life'} />
      <div className="flex flex-col gap-4 px-4 py-4">
        <div className={`rounded-2xl p-3 text-xs ${isYouth ? 'bg-amber-100 text-center font-black text-amber-900' : 'bg-brand-50 text-brand-800'}`}>
          {isYouth ? '🍪 Each step earns 1 Food Bite. Finish the whole routine for +5 bonus bites!' : 'Set routines to repeat every day, weekdays, weekly or on selected days.'}
        </div>

        {visibleRoutines.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-center text-sm text-slate-400">No routines due here.</div>}

        {visibleRoutines.map((routine) => {
          const completed = routine.steps.filter((step) => step.done).length
          const expanded = activeId === routine.id || interfaceMode !== 'visual' || isYouth
          const repeatLabel = routine.repeat === 'selected_days' && routine.selectedDays.length > 0
            ? routine.selectedDays.join(', ')
            : routine.repeat.replace('_', ' ')
          return (
            <section key={routine.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
              <button className="w-full text-left" onClick={() => setActiveId(activeId === routine.id ? null : routine.id)}>
                <div className="flex items-start justify-between gap-3"><div><p className={`${isYouth ? 'text-xl' : 'text-base'} font-extrabold text-slate-900`}>{routine.title}</p>{!isYouth && <p className="mt-1 text-xs text-slate-500">{routine.category} · {repeatLabel} · {routine.style.replace('_', ' ')}</p>}</div><span className="rounded-full bg-brand-50 px-3 py-1.5 text-sm font-bold text-brand-700">{completed}/{routine.steps.length}</span></div>
              </button>

              {expanded && <div className={`mt-4 ${interfaceMode === 'visual' ? 'flex flex-col gap-4' : 'flex flex-col gap-2'}`}>
                {routine.steps.map((step, index) => (
                  <div key={step.id} className={`${interfaceMode === 'visual' ? 'rounded-3xl border-2 p-5' : 'rounded-xl bg-slate-50 p-3'} ${step.done ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'}`}>
                    <button onClick={() => complete(routine.id, step.id)} className="flex w-full items-start gap-3 text-left">
                      <span className={`${interfaceMode === 'visual' ? 'text-4xl' : 'text-xl'}`}>{step.icon || (step.done ? '✅' : '⬜')}</span>
                      <div className="flex-1"><p className={`${interfaceMode === 'visual' ? 'text-xl' : 'text-sm'} font-bold ${step.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{interfaceMode !== 'visual' && <span className="mr-1 text-slate-400">{index + 1}.</span>}{step.text}</p>{step.time && <p className="mt-1 text-xs font-semibold text-brand-600">{step.time}</p>}{step.instructions && <p className="mt-1 text-xs text-slate-500">{step.instructions}</p>}{step.done && step.completionKind && <p className="mt-1 text-xs font-semibold text-emerald-700">{completionLabels[step.completionKind]}</p>}</div>
                    </button>
                    {!step.done && currentUser.role === 'participant' && !isYouth && <div className="mt-3 grid grid-cols-3 gap-1.5">{(Object.keys(completionLabels) as CompletionKind[]).map((kind) => <button key={kind} onClick={() => complete(routine.id, step.id, kind)} className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-[10px] font-semibold text-slate-600">{completionLabels[kind]}</button>)}</div>}
                  </div>
                ))}
                {routine.steps.some((step) => step.done) && !isYouth && <button onClick={() => resetRoutine(routine.id)} className="mt-1 text-xs font-bold text-brand-600">Reset routine now</button>}
              </div>}
            </section>
          )
        })}

        {canCreate && <button onClick={() => setShowCreate(true)} className="rounded-2xl bg-brand-600 py-3 text-sm font-bold text-white shadow-card">+ Create a Routine</button>}
      </div>

      {showCreate && <RoutineCreator onClose={() => setShowCreate(false)} onSave={(routine) => { persist([...routines, routine]); setShowCreate(false) }} />}
    </div>
  )
}

function RoutineCreator({ onClose, onSave }: { onClose: () => void; onSave: (routine: Routine) => void }) {
  const { currentUser } = useApp()
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('Daily routine')
  const [style, setStyle] = useState<RoutineStyle>('checklist')
  const [repeat, setRepeat] = useState<RoutineRepeat>('daily')
  const [selectedDays, setSelectedDays] = useState<RoutineDay[]>([])
  const [steps, setSteps] = useState([''])

  const toggleDay = (day: RoutineDay) => {
    setSelectedDays((previous) => previous.includes(day) ? previous.filter((value) => value !== day) : [...previous, day])
  }

  const submit = () => {
    const clean = steps.map((step) => step.trim()).filter(Boolean)
    if (!title.trim() || clean.length === 0) return
    if ((repeat === 'selected_days' || repeat === 'weekly') && selectedDays.length === 0) return
    onSave({
      id: `routine-${Date.now()}`,
      title: title.trim(),
      category,
      style,
      repeat,
      selectedDays,
      createdByUserId: currentUser.id,
      createdAt: new Date().toISOString(),
      lastResetDate: localISODate(),
      steps: clean.map((text, index) => ({ id: `routine-step-${Date.now()}-${index}`, text, done: false })),
    })
  }

  return <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4"><div className="mx-auto mt-10 max-w-md rounded-3xl bg-white p-5 shadow-xl"><h2 className="text-lg font-extrabold text-slate-900">Create a Routine</h2><div className="mt-4 flex flex-col gap-3"><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Routine name" /><select className="input" value={category} onChange={(e) => setCategory(e.target.value)}><option>Daily routine</option><option>Morning</option><option>Afternoon</option><option>Evening</option><option>Bedtime</option><option>Personal care</option></select><select className="input" value={style} onChange={(e) => setStyle(e.target.value as RoutineStyle)}><option value="checklist">Simple checklist</option><option value="step_by_step">Step by step</option><option value="visual">Visual routine</option><option value="timed">Timed routine</option></select><label className="text-xs font-bold text-slate-600">Repeat<select className="input mt-1" value={repeat} onChange={(e) => { setRepeat(e.target.value as RoutineRepeat); setSelectedDays([]) }}><option value="daily">Ongoing · Every day</option><option value="weekdays">Ongoing · Weekdays</option><option value="weekly">Ongoing · Weekly</option><option value="selected_days">Ongoing · Selected days</option><option value="none">One-off · No repeat</option></select></label>{(repeat === 'selected_days' || repeat === 'weekly') && <div><p className="mb-2 text-xs font-bold text-slate-600">{repeat === 'weekly' ? 'Choose the day each week' : 'Choose the days this repeats'}</p><div className="grid grid-cols-4 gap-2">{dayOptions.map(({ key, label }) => <button type="button" key={key} onClick={() => repeat === 'weekly' ? setSelectedDays([key]) : toggleDay(key)} className={`rounded-xl border px-2 py-2 text-xs font-bold ${selectedDays.includes(key) ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>{label}</button>)}</div>{selectedDays.length === 0 && <p className="mt-2 text-xs font-semibold text-amber-600">Choose at least one day.</p>}</div>}{steps.map((step, index) => <input key={index} className="input" value={step} onChange={(e) => setSteps((previous) => previous.map((value, i) => i === index ? e.target.value : value))} placeholder={`Step ${index + 1}`} />)}<button onClick={() => setSteps([...steps, ''])} className="text-xs font-bold text-brand-600">+ Add another step</button></div><div className="mt-5 flex gap-2"><button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3">Cancel</button><button onClick={submit} className="flex-1 rounded-xl bg-brand-600 py-3 font-bold text-white">Save routine</button></div></div></div>
}

